import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function orgLabel(org) {
  if (!org) return '';
  return org.name || org.slug || `org #${org.id}`;
}

export default function TenantSwitcher() {
  const { user, switchOrg, clearOrgOverride } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customId, setCustomId] = useState('');
  const [error, setError] = useState(null);
  // The switcher lives inside the account menu, which itself hangs off the
  // bottom of the nav rail — dropping the org list downwards ran it off the
  // bottom of the window and made the entries unclickable. Default to
  // opening upwards and only fall back to downwards when there genuinely
  // isn't room above (e.g. the phone top bar, where the menu starts high).
  const [dropUp, setDropUp] = useState(true);
  const [maxHeight, setMaxHeight] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const orgs = user?.orgs || [];
  const isSuperAdmin = !!user?.is_super_admin;
  const activeOrgId = user?.active_org_id || user?.org_id || null;
  const primaryOrgId = user?.primary_org_id || user?.org_id || null;
  const isOverridden = !!user?.org_override;

  const visible = isSuperAdmin || orgs.length > 1;

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    function place() {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      // `position: static` at narrow widths (see App.css) — the menu is in
      // flow there, so there's no direction to pick.
      if (window.getComputedStyle(menu).position === 'static') {
        setMaxHeight(null);
        return;
      }
      const rect = trigger.getBoundingClientRect();
      // scrollHeight, not offsetHeight: the cap we set below would otherwise
      // feed back into the measurement and make the placement oscillate.
      const needed = menu.scrollHeight + 6;
      const above = rect.top;
      const below = window.innerHeight - rect.bottom;
      const up = above >= needed || above >= below;
      setDropUp(up);
      setMaxHeight(Math.max(160, (up ? above : below) - 12));
    }

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, orgs.length, isSuperAdmin, isOverridden, error]);

  if (!visible) return null;

  const activeOrg =
    orgs.find((o) => String(o.id) === String(activeOrgId)) || null;
  const activeLabel = activeOrg
    ? orgLabel(activeOrg)
    : activeOrgId
      ? `org ${activeOrgId}`
      : 'Select org';

  async function handleSwitch(orgId) {
    if (!orgId || String(orgId) === String(activeOrgId)) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await switchOrg(String(orgId));
      setOpen(false);
      setCustomId('');
      // Reload so all in-flight queries (dashboard, brands, etc.) refetch
      // against the new org. Cheaper than threading org_id through every
      // page's data layer.
      window.location.reload();
    } catch (e) {
      setError(e.message || 'Failed to switch org');
      setBusy(false);
    }
  }

  async function handleClear() {
    setBusy(true);
    setError(null);
    try {
      await clearOrgOverride();
      setOpen(false);
      window.location.reload();
    } catch (e) {
      setError(e.message || 'Failed to reset org');
      setBusy(false);
    }
  }

  function handleCustomSubmit(e) {
    e.preventDefault();
    const id = customId.trim();
    if (!id) return;
    handleSwitch(id);
  }

  return (
    <div className="tenant-switcher" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`tenant-switcher-trigger${isOverridden ? ' is-override' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        title={isOverridden ? 'Viewing as another org (super admin)' : 'Active organization'}
      >
        <Building2 size={16} />
        <span className="tenant-switcher-label">{activeLabel}</span>
        {isOverridden && <span className="tenant-switcher-badge">override</span>}
        {open && dropUp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div
          className={`tenant-switcher-menu${dropUp ? ' is-up' : ''}`}
          role="menu"
          ref={menuRef}
          style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
        >
          <div className="tenant-switcher-section-title">Your organizations</div>
          {orgs.length === 0 && (
            <div className="tenant-switcher-empty">No orgs in your token</div>
          )}
          {orgs.map((org) => {
            const active = String(org.id) === String(activeOrgId);
            return (
              <button
                key={org.id}
                type="button"
                className={`tenant-switcher-item${active ? ' is-active' : ''}`}
                onClick={() => handleSwitch(org.id)}
                disabled={busy}
                role="menuitem"
              >
                <span className="tenant-switcher-item-main">
                  <span className="tenant-switcher-item-name">{orgLabel(org)}</span>
                  {org.role && (
                    <span className="tenant-switcher-item-role">{org.role}</span>
                  )}
                </span>
                {active && <Check size={14} />}
              </button>
            );
          })}

          {isSuperAdmin && (
            <>
              <div className="tenant-switcher-divider" />
              <div className="tenant-switcher-section-title">
                Super admin: switch to any org
              </div>
              <form
                className="tenant-switcher-custom"
                onSubmit={handleCustomSubmit}
              >
                <input
                  type="text"
                  placeholder="org id"
                  value={customId}
                  onChange={(e) => setCustomId(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
                <button type="submit" disabled={busy || !customId.trim()}>
                  Switch
                </button>
              </form>
            </>
          )}

          {isOverridden && primaryOrgId && (
            <>
              <div className="tenant-switcher-divider" />
              <button
                type="button"
                className="tenant-switcher-item tenant-switcher-reset"
                onClick={handleClear}
                disabled={busy}
                role="menuitem"
              >
                <RotateCcw size={14} />
                <span>Reset to primary ({primaryOrgId})</span>
              </button>
            </>
          )}

          {error && <div className="tenant-switcher-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
